import React, { useState } from "react"
import { Button, Input } from "@aix/ui"
import { Send, Loader2, MessageSquare, X } from "lucide-react"
import { trpc } from "../../../lib/trpc"
import toast from "react-hot-toast"

interface ChatSidebarProps {
  isOpen: boolean
  onClose: () => void
  selectedSystemId: string | null
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({ isOpen, onClose, selectedSystemId }) => {
  const [input, setInput] = useState("")
  const [messages, setMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([])

  const chatMutation = trpc.databrain.chatWithSchema.useMutation({
    onSuccess: (data) => {
      setMessages((prev) => [...prev, { role: "assistant", content: data.response }])
    },
    onError: (error) => {
      toast.error(`Failed to get response: ${error.message}`)
    },
  })

  const handleSend = () => {
    if (!selectedSystemId) {
      toast.error("Please select a system first")
      return
    }
    if (!input.trim()) {
      toast.error("Please enter a message")
      return
    }

    // Add user message
    const userMessage = input.trim()
    setMessages((prev) => [...prev, { role: "user", content: userMessage }])
    setInput("")

    // Send to LLM
    chatMutation.mutate({
      systemId: selectedSystemId,
      message: userMessage,
    })
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleClearConversation = () => {
    setMessages([])
    toast.success("Conversation cleared")
  }

  return (
    <div
      className={`
        h-full border-l border-border bg-background
        transition-all duration-300 ease-in-out
        ${isOpen ? "w-[400px]" : "w-0"}
        overflow-hidden flex flex-col
      `}
    >
      {/* Header */}
      <div className="border-b border-border bg-secondary px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Schema Chat</h3>
        </div>
        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <button onClick={handleClearConversation} className="text-xs text-muted-foreground hover:text-foreground">
              Clear
            </button>
          )}
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center px-4">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">Ask questions about your database schema</p>
              <p className="text-xs text-muted-foreground">&quot;What tables are related to users?&quot;</p>
            </div>
          </div>
        ) : (
          messages.map((message, idx) => (
            <div key={idx} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                  message.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"
                }`}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))
        )}
        {chatMutation.isLoading && (
          <div className="flex justify-start">
            <div className="bg-secondary px-3 py-2 rounded-lg">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="border-t border-border bg-background p-3">
        <div className="flex gap-2">
          <Input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={selectedSystemId ? "Ask about your schema..." : "Select a system first"}
            disabled={!selectedSystemId || chatMutation.isLoading}
            className="flex-1 text-sm"
          />
          <Button
            onClick={handleSend}
            disabled={!selectedSystemId || !input.trim() || chatMutation.isLoading}
            loading={chatMutation.isLoading}
            size="sm"
          >
            <Send className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ChatSidebar
